import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, inject, signal, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import WaveSurfer from 'wavesurfer.js';
import copy from 'clipboard-copy';
import { BEARER_TOKEN_OVERRIDE } from '@core/interceptors/credentials.interceptor';
import { environment } from '@env/environment';

// PrimeNG imports
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DrawerModule } from 'primeng/drawer';
import { MenuItem } from 'primeng/api';

// Services and models
import { AgentsService } from '@generated/services/agents.service';
import { SessionsService } from '@generated/services/sessions.service';
import { TelecomService } from '@generated/services/telecom.service';
import { ModelsService } from '@generated/services/models.service';
import { AgentSessionDto } from '@generated/models/agent-session-dto';
import { CallDto } from '@generated/models/call-dto';
import { AgentSessionStatus } from '@generated/models/agent-session-status';
import { ChatMessageDto } from '@generated/models/chat-message-dto';
import { TestStatus } from '@generated/models/test-status';
import { EvalCompletionReason } from '@generated/models/eval-completion-reason';
import { GetModelsHierarchyResult } from '@generated/models/get-models-hierarchy-result';
import { OpenApiPropertyValueEntry } from '@generated/models/open-api-property-value-entry';
import { NotificationService } from '@core/services/notification.service';
import { AuthService, TokenSource } from '@core/services/auth.service';
import { Signalr } from '@core/services/signalr';
import { ChatHistoryViewComponent } from '@shared/components/chat-history-view/chat-history-view.component';
import { LiveListenButtonComponent } from '@shared/components/live-listen-button/live-listen-button.component';
import { ModelSelectorButton } from '@shared/components/model-selector-button/model-selector-button';
import { TesterSelection } from '@shared/components/model-tester/model-tester.component';
import { dictToValueEntries, valueEntriesToDict } from '@shared/utils/openapi-schema-utils';

@Component({
  selector: 'app-conversation-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TabsModule,
    ButtonModule,
    MenuModule,
    ProgressSpinnerModule,
    TooltipModule,
    TagModule,
    MessageModule,
    DrawerModule,
    ChatHistoryViewComponent,
    LiveListenButtonComponent,
    ModelSelectorButton,
  ],
  templateUrl: './conversation-detail.html',
  styleUrl: './conversation-detail.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConversationDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private agentsService = inject(AgentsService);
  private sessionsService = inject(SessionsService);
  private telecomService = inject(TelecomService);
  private modelsService = inject(ModelsService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);

  agentId: string = '';
  sessionId: string = '';
  /** Session-scoped bearer token из ?sessionToken=... — используется вместо
   *  cookie-based авторизации, когда компонент открыт публично (например
   *  из agent-test share-ссылки после завершения сессии). Без него бэк
   *  вернёт 401. */
  private sessionToken: string | null = null;
  session: AgentSessionDto | null = null;
  isLoading = true;

  // WaveSurfer
  private wavesurfer: WaveSurfer | null = null;

  // Audio controls
  isPlaying = false;
  isAudioLoading = true;
  audioLoadError = false;
  playbackRate = 1.0;
  currentTime = 0;
  duration = 0;

  // Menu items — полное меню для audio-controls (регулярная сессия)
  menuItems: MenuItem[] = [];
  /** Компактное меню для .test-info-card: только два admin synth-пункта.
   *  У test-сессий audio-плеера нет, значит обычные download-пункты все
   *  дисейблены — показывать их бессмысленно. */
  synthOnlyMenuItems: MenuItem[] = [];

  /** Admin? Проверяется по Original-токену (impersonation'у не поддаётся —
   *  админ подменяющий юзера всё равно админ). Определяет видимость
   *  «Сгенерировать синтетическую озвучку» + доступ к synth-drawer'у. */
  readonly isAdmin = this.authService.hasClientRole('admin', TokenSource.Original);

  // ==== Synthetic user audio (admin) ====
  /** Drawer open flag. Модель-селектор + Generate живут внутри. */
  showSynthDrawer = false;
  /** Дерево TTS моделей — грузится лениво при первом открытии drawer'а. */
  modelsHierarchy: GetModelsHierarchyResult | null = null;
  modelsIncludeAll = false;
  /** Выбранная TTS version / voice / additional_properties — стейт drawer'а
   *  до нажатия Generate. Ничего не персистится клиентом: как только запрос
   *  ушёл, значения летят в бэк одним POST'ом. */
  synthTtsVersionId: string | undefined = undefined;
  synthTtsVoiceId: string | undefined = undefined;
  synthTtsProperties: { [key: string]: any } = {};
  /** История, которую бэк использует для синтеза юзер-стороны. Сидится из
   *  session.history при открытии drawer'а, дальше редактируется юзером
   *  через chat-history-view в editable-режиме. Клонируем при сиде —
   *  chat-history-view эмитит НОВЫЙ массив каждый раз, но исходный DTO
   *  session.history не должен мутироваться (мы его отдельно рендерим
   *  в Transcription-табе). */
  synthChatHistory: ChatMessageDto[] = [];
  isSynthesizing = false;

  readonly selectTtsModelLabel = $localize`:@@convDetail.synth.selectTts:Select TTS Model`;

  readonly unknownAgentLabel = $localize`:@@conversations.unknownAgent:Unknown Agent`;
  readonly agentFallbackLabel = $localize`:@@agentTest.agent:Agent`;
  readonly campaignFallbackLabel = $localize`:@@convDetail.campaign.fallback:Campaign`;
  readonly retryLabel = $localize`:@@common.retry:Retry`;
  readonly loadingLabel = $localize`:@@common.loading:Loading...`;
  readonly playLabel = $localize`:@@convDetail.play:Play`;
  readonly pauseLabel = $localize`:@@convDetail.pause:Pause`;

  ngOnInit(): void {
    this.agentId = this.route.snapshot.paramMap.get('agentId') || '';
    this.sessionId = this.route.snapshot.paramMap.get('sessionId') || '';
    this.sessionToken = this.route.snapshot.queryParamMap.get('sessionToken');

    this.rebuildMenuItems();

    if (this.agentId && this.sessionId) {
      this.loadSession();
      this.loadCalls();
      this.subscribeToSessionUpdates();
    }
  }

  /** SignalR AgentSessionUpdated — бэк рассылает событие при любом
   *  изменении сессии (start / message / synthesize done / finalize).
   *  DTO приходит ПОЛНЫЙ (AgentSession и AgentSessionHistory схлопнули
   *  в один AgentSessionDto с history + audio_url + synthesized_*),
   *  поэтому применяем напрямую — GET не нужен. */
  private subscribeToSessionUpdates(): void {
    this.signalr.agentSessionUpdated$
      .pipe(
        filter((s) => s.id === this.sessionId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((session) => this.applySession(session));
  }

  /** Мягкий refresh без spinner'а — вызывается вручную (напр. после
   *  нажатия Generate) когда нужно достать свежий snapshot без ожидания
   *  SignalR-события. */
  private reloadSession(): void {
    this.sessionsService.apiSessionsSessionIdGet({
      sessionId: this.sessionId,
    }, this.sessionAuthContext()).subscribe({
      next: (result) => {
        if (result.data) this.applySession(result.data);
      },
      error: (error) => {
        console.warn('Failed to reload session on update:', error);
      },
    });
  }

  /** Применяем свежий session-snapshot (из GET или SignalR) — обновляем
   *  session, сбрасываем локальные in-flight flags (server-state теперь
   *  авторитативный), пересобираем меню. */
  private applySession(session: AgentSessionDto): void {
    this.session = session;
    if (this.isSynthesizing) this.isSynthesizing = false;
    this.rebuildMenuItems();
    this.cdr.markForCheck();
  }

  /** HttpContext с session-token override, если он передан в URL. Иначе
   *  undefined — interceptor подставит cookie-based auth (админский путь). */
  private sessionAuthContext(): HttpContext | undefined {
    return this.sessionToken
      ? new HttpContext().set(BEARER_TOKEN_OVERRIDE, this.sessionToken)
      : undefined;
  }

  /** Публичный режим = мы попали сюда через /published/... с session-token'ом.
   *  В этом режиме все внутренние ссылки на агента должны вести на
   *  /published/agents/:id (публичный тестер), а не на админскую страницу
   *  редактирования — юзер там не залогинен. */
  get isPublicMode(): boolean {
    return !!this.sessionToken;
  }

  get agentRouterLink(): any[] {
    return this.isPublicMode
      ? ['/published/agents', this.agentId]
      : ['/agents', this.agentId];
  }

  /**
   * SKN-363: link-builder на карточку агента ПО ID из msg.agent.id — компонент
   * chat-history-view дёргает эту функцию на каждый turn и подставляет id
   * реального автора реплики (в handoff-цепочке разные агенты). Раньше
   * `agentRouterLink` вёл на «основного» агента сессии, теперь per-turn.
   * Стрелочная функция, чтобы `this` был захвачен: input компонента получает
   * саму функцию, не bound reference.
   */
  readonly agentRouterLinkFor = (agentId: string): any[] => this.isPublicMode
    ? ['/published/agents', agentId]
    : ['/agents', agentId];

  /** Звонки этого разговора — для ссылок на карточки звонков во вкладке
   *  Phone call. Обычно один, но после перевода на оператора их два: нога
   *  клиента и нога оператора.
   *
   *  Пусто в публичном режиме: /api/Telecom/calls живёт под [Authorize], а у
   *  владельца share-ссылки есть только session-token. Молча не грузим, вместо
   *  того чтобы ловить 401 и пугать пользователя ошибкой. */
  readonly calls = signal<CallDto[]>([]);

  private loadCalls(): void {
    if (this.isPublicMode || !this.sessionId) return;

    this.telecomService.apiTelecomCallsGet({ agentSessionId: this.sessionId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Фильтруем ещё раз на клиенте: бэк без поддержки agentSessionId
        // молча проигнорирует параметр и отдаст все звонки пространства —
        // в карточке появились бы ссылки на чужие звонки. Проверка дешёвая,
        // а порядок мержа перестаёт быть условием правильности.
        next: (result) => this.calls.set(
          (result.calls || []).filter(c => c.agent_session_id === this.sessionId)
        ),
        // Ссылки на звонки — дополнение к карточке, не её смысл: если список
        // не пришёл, вкладка должна остаться рабочей.
        error: (error) => console.warn('Failed to load calls of the session:', error),
      });
  }

  private rebuildMenuItems(): void {
    // Все URL'ы (merged + 3 stem'а) уже лежат в session DTO — бэк отдаёт
    // pre-signed ссылки в общем GET /sessions/{id}. Отдельные /audio/agent,
    // /audio/user-raw, /audio/user-cleaned — не нужны на этой странице:
    // если url отсутствует, значит бэк ещё не собрал этот stem, дисейблим
    // пункт меню.
    this.menuItems = [
      {
        label: $localize`:@@convDetail.menu.download:Download audio`,
        icon: 'pi pi-download',
        disabled: !this.session?.audio_url || this.audioLoadError,
        command: () => this.openInNewTab(this.session?.audio_url)
      },
      {
        label: $localize`:@@convDetail.menu.downloadAgent:Download agent audio`,
        icon: 'pi pi-microphone',
        disabled: !this.session?.agent_audio_url,
        command: () => this.openInNewTab(this.session?.agent_audio_url)
      },
      {
        label: $localize`:@@convDetail.menu.downloadUserRaw:Download user audio`,
        icon: 'pi pi-user',
        disabled: !this.session?.user_raw_audio_url,
        command: () => this.openInNewTab(this.session?.user_raw_audio_url)
      },
      {
        label: $localize`:@@convDetail.menu.downloadUserCleaned:Download cleaned user audio`,
        icon: 'pi pi-user-plus',
        disabled: !this.session?.user_cleaned_audio_url,
        command: () => this.openInNewTab(this.session?.user_cleaned_audio_url)
      },
      {
        label: $localize`:@@convDetail.menu.copyTranscript:Copy transcript`,
        icon: 'pi pi-copy',
        command: () => this.copyTranscript()
      },
      {
        label: $localize`:@@convDetail.menu.copySessionUrl:Copy session URL`,
        icon: 'pi pi-link',
        disabled: !this.session?.session_token,
        command: () => this.copySessionUrl()
      }
    ];

    // Admin-only: только «Сгенерировать» (открывает synth-drawer). Скачать
    // готовое аудио — кнопка в самом drawer'е (не в меню), чтобы не
    // засорять список и не дублировать функциональность. synthOnlyMenuItems
    // используется на test-сессиях, где audio-плеера и download-пунктов нет.
    // Copy session URL — доступен всем (не admin-only).
    this.synthOnlyMenuItems = [
      {
        label: $localize`:@@convDetail.menu.copySessionUrl:Copy session URL`,
        icon: 'pi pi-link',
        disabled: !this.session?.session_token,
        command: () => this.copySessionUrl()
      }
    ];
    if (this.isAdmin) {
      // Активная сессия / dispatching — синтезировать нечего (записи ещё
      // нет). После завершения (Completed/Failed/Cancelled/Timeout) — можно.
      // Test-сессии могут не иметь agent_joined_at, поэтому этот флаг НЕ
      // используем — только status. status=null считаем ok (старые сессии).
      const activeStatus = this.session?.status;
      const stillRunning = activeStatus === 'Pending'
        || activeStatus === 'Dispatching'
        || activeStatus === 'Active';
      const generateSynth: MenuItem = {
        label: this.synthInProgress
          ? $localize`:@@convDetail.menu.synthInProgress:Synthesizing…`
          : $localize`:@@convDetail.menu.synth:Generate synthetic audio`,
        icon: 'pi pi-sparkles',
        disabled: stillRunning,
        command: () => this.openSynthDrawer(),
      };
      this.menuItems.push(generateSynth);
      this.synthOnlyMenuItems.push(generateSynth);
    }
  }

  private openInNewTab(url?: string | null): void {
    if (url) window.open(url, '_blank');
  }

  /** Сид всех полей drawer'а из текущей session. Дёргается при первом
   *  открытии и при нажатии Refresh — локальные правки перетираются,
   *  что и является ожидаемым поведением («откатить к серверному»).
   *  История: приоритет у synthesized_chat_history (правки прошлого
   *  синта) → fallback на session.history (оригинал). Клонируем каждое
   *  сообщение, чтобы editable-chat-history-view (immutable-стиль
   *  через spread) не мутировал исходный DTO во время редактирования.
   *  TTS-настройки: подставляем прошлые synthesized_user_tts_*, если
   *  бэк их вернул. Пусто → пустая форма (первый раз для этой сессии). */
  private seedSynthFromSession(): void {
    const source = this.session?.synthesized_chat_history ?? this.session?.history ?? [];
    this.synthChatHistory = source.map(m => ({ ...m }));
    this.synthTtsVersionId = this.session?.synthesized_user_tts_version_id ?? undefined;
    this.synthTtsVoiceId = this.session?.synthesized_user_tts_voice_id ?? undefined;
    this.synthTtsProperties = valueEntriesToDict(this.session?.synthesized_user_tts_additional_properties);
  }

  /** Открывает drawer с TTS model-selector'ом. Дерево моделей грузится
   *  один раз лениво (при первом открытии), потом переиспользуется. */
  openSynthDrawer(): void {
    this.showSynthDrawer = true;
    this.seedSynthFromSession();
    if (!this.modelsHierarchy) {
      this.modelsService.apiModelsHierarchyGet({ includeAll: this.modelsIncludeAll }).subscribe({
        next: (h) => {
          this.modelsHierarchy = h;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load models hierarchy:', err);
          this.notificationService.showError(
            $localize`:@@convDetail.notify.modelsLoadFail:Failed to load models`,
          );
        },
      });
    }
  }

  closeSynthDrawer(): void {
    this.showSynthDrawer = false;
    this.cdr.markForCheck();
  }

  /** Перезагрузить сессию с бэка и пере-сидировать состояние drawer'а
   *  из свежих данных. Локальные правки перетираются — юзер жмёт Refresh
   *  осознанно (напр. после того как коллега добавил новую synth-версию
   *  через MCP-тул). Использует общий reloadSession() под капотом,
   *  но чейнит seedSynthFromSession в success. `isReloadingSynth`
   *  показывает спиннер на кнопке пока запрос летит. */
  isReloadingSynth = false;
  reloadSynth(): void {
    if (this.isReloadingSynth) return;
    this.isReloadingSynth = true;
    this.cdr.markForCheck();
    this.sessionsService.apiSessionsSessionIdGet({
      sessionId: this.sessionId,
    }, this.sessionAuthContext()).subscribe({
      next: (result) => {
        this.session = result.data || null;
        // Серверное состояние теперь свежее — сбрасываем локальный
        // isSynthesizing (тот же приём, что в reloadSession).
        if (this.isSynthesizing) this.isSynthesizing = false;
        this.seedSynthFromSession();
        this.rebuildMenuItems();
        this.isReloadingSynth = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to reload session in synth drawer:', err);
        this.notificationService.showError(
          $localize`:@@convDetail.notify.loadFail:Failed to load session`,
        );
        this.isReloadingSynth = false;
        this.cdr.markForCheck();
      },
    });
  }

  onModelsIncludeAllChange(includeAll: boolean): void {
    this.modelsIncludeAll = includeAll;
    // Пере-грузим дерево с новым фильтром — селектор перерисует список.
    this.modelsService.apiModelsHierarchyGet({ includeAll }).subscribe({
      next: (h) => {
        this.modelsHierarchy = h;
        this.cdr.markForCheck();
      },
    });
  }

  /** Выбор через быстрый Model → сразу применяем versionId (голос и
   *  additional_properties остаются из предыдущего теста). */
  onSynthModelSelected(versionId: string): void {
    this.synthTtsVersionId = versionId;
    this.cdr.markForCheck();
  }

  /** Выбор через полный тестер (Save from tester) — прилетает объект с
   *  версией + голосом + additional_properties. Тестер прогонял голос
   *  на живом сэмпле, доверяем полному пакету. */
  onSynthTesterSelected(selection: TesterSelection): void {
    this.synthTtsVersionId = selection.versionId;
    this.synthTtsVoiceId = selection.voiceId ?? undefined;
    this.synthTtsProperties = selection.additionalProperties ?? {};
    this.cdr.markForCheck();
  }

  /** Отправляет POST /audio/synthesize. Бэк возвращает job_id и запускает
   *  Hangfire-джобу; результат прилетит через SignalR AgentSessionUpdated,
   *  наш subscribeToSessionUpdates подхватит и обновит session.audio_url. */
  generateSyntheticAudio(): void {
    if (!this.synthTtsVersionId || this.isSynthesizing) return;
    this.isSynthesizing = true;
    this.cdr.markForCheck();

    const body = {
      user_tts_version_id: this.synthTtsVersionId,
      user_tts_voice_id: this.synthTtsVoiceId,
      user_tts_additional_properties: dictToValueEntries(this.synthTtsProperties) as OpenApiPropertyValueEntry[],
      // Массив, отредактированный админом в chat-history-view (или без правок
      // = копия session.history). Пусто/null означало бы «синтезировать
      // сессию как есть без явных правок» — но мы всегда шлём полный
      // slice, backend может свободно интерпретировать.
      synthesized_chat_history: this.synthChatHistory,
    };

    this.agentsService.apiAgentsAgentIdSessionsSessionIdAudioSynthesizePost({
      agentId: this.agentId,
      sessionId: this.sessionId,
      body,
    }, this.sessionAuthContext()).subscribe({
      next: (result) => {
        if (result?.success) {
          // Drawer НЕ закрываем — юзер видит спиннер до прилёта
          // audio_url'а через SignalR. isSynthesizing НЕ сбрасываем
          // здесь: между этим success'ом и завершением reload'а мог
          // быть race window, где ни локальный флаг, ни серверный
          // job_id не активны → кнопка Generate на мгновение
          // разлочится. Сбрасываем isSynthesizing внутри reloadSession
          // (там серверное состояние уже авторитативно). Через
          // SignalR прилетит финальный apid_url — синт-in-progress
          // getter станет false, спиннер сменится на кнопку «Скачать».
          this.reloadSession();
        } else {
          this.notificationService.showError(
            result?.error || $localize`:@@convDetail.notify.synthFail:Failed to start synthesis`,
          );
          this.isSynthesizing = false;
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('Failed to synthesize audio:', err);
        this.notificationService.showError(
          $localize`:@@convDetail.notify.synthFail:Failed to start synthesis`,
        );
        this.isSynthesizing = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** True когда синтез в процессе: либо только что нажали Generate (POST
   *  ещё летит), либо бэк уже дал hangfire_job_id, но результата ещё нет.
   *  Определяет спиннер в drawer'е и label «Синтез в процессе…» в меню. */
  get synthInProgress(): boolean {
    if (this.isSynthesizing) return true;
    return !!this.session?.synthesized_hangfire_job_id && !this.session?.synthesized_audio_url;
  }

  downloadSynth(): void {
    this.openInNewTab(this.session?.synthesized_audio_url);
  }

  /** Прокидываем в model-selector-button как currentAdditionalProperties
   *  (компонент ждёт flat dict). Значения хранятся в UI-стейте как dict. */
  getSynthPropertiesDict(): { [key: string]: any } {
    return this.synthTtsProperties;
  }

  ngAfterViewInit(): void {
    // WaveSurfer будет инициализирован после загрузки данных
  }

  ngOnDestroy(): void {
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
  }

  private loadSession(): void {
    this.isLoading = true;

    this.sessionsService.apiSessionsSessionIdGet({
      sessionId: this.sessionId
    }, this.sessionAuthContext()).subscribe({
      next: (result) => {
        this.session = result.data || null;
        this.duration = this.session?.duration_seconds || 0;
        this.isLoading = false;
        this.rebuildMenuItems();
        this.cdr.markForCheck();

        // WaveSurfer поднимаем, когда есть что рисовать: у e2e-прогона
        // запись такая же, как у обычной сессии, а у text-only симуляции
        // её нет вовсе.
        if (this.hasAudio()) {
          setTimeout(() => this.initWaveSurfer(), 100);
        }
      },
      error: (error) => {
        console.error('Failed to load session:', error);
        this.notificationService.showError($localize`:@@convDetail.notify.loadFail:Failed to load session`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private initWaveSurfer(): void {
    if (!this.session?.audio_url) {
      // Audio URL not available yet
      this.isAudioLoading = false;
      this.audioLoadError = true;
      this.updateMenuItemsDisabled();
      this.notificationService.showWarning($localize`:@@convDetail.notify.audioNotReady:Audio recording is not ready yet. Please try again later.`);
      this.cdr.markForCheck();
      return;
    }

    // Reset states
    this.isAudioLoading = true;
    this.audioLoadError = false;
    this.updateMenuItemsDisabled();

    // Destroy existing instance if any
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }

    this.wavesurfer = WaveSurfer.create({
      container: '#waveform',
      waveColor: 'rgb(200, 200, 200)',
      progressColor: 'rgb(100, 100, 200)',
      height: 100,
      barWidth: 2,
      barGap: 1,
      barRadius: 2
    });

    this.wavesurfer.load(this.session.audio_url);

    // Обработка событий
    this.wavesurfer.on('ready', () => {
      this.duration = this.wavesurfer?.getDuration() || 0;
      this.isAudioLoading = false;
      this.audioLoadError = false;
      this.updateMenuItemsDisabled();
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('error', (error) => {
      console.error('WaveSurfer error:', error);
      this.isAudioLoading = false;
      this.audioLoadError = true;
      this.updateMenuItemsDisabled();
      this.notificationService.showError($localize`:@@convDetail.notify.audioLoadFail:Failed to load audio file`);
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('play', () => {
      this.isPlaying = true;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('pause', () => {
      this.isPlaying = false;
      this.cdr.markForCheck();
    });

    this.wavesurfer.on('timeupdate', (currentTime) => {
      this.currentTime = currentTime;
      this.cdr.markForCheck();
    });
  }

  private updateMenuItemsDisabled(): void {
    this.rebuildMenuItems();
  }

  retryAudioLoad(): void {
    // Reset error state
    this.audioLoadError = false;
    this.isAudioLoading = true;
    this.cdr.markForCheck();

    // Отдельного audio-endpoint'а больше нет — audio_url приходит в
    // AgentSessionDto. Перезапрашиваем сессию, оттуда берём URL.
    this.sessionsService.apiSessionsSessionIdGet({
      sessionId: this.sessionId
    }, this.sessionAuthContext()).subscribe({
      next: (result) => {
        if (result.data) this.session = result.data;
        if (this.session?.audio_url) {
          this.initWaveSurfer();
        } else {
          this.isAudioLoading = false;
          this.audioLoadError = true;
          this.notificationService.showWarning($localize`:@@convDetail.notify.audioProcessing:Audio is still being processed. Please try again in a moment.`);
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error fetching audio:', error);
        this.isAudioLoading = false;
        this.audioLoadError = true;
        this.notificationService.showError($localize`:@@convDetail.notify.audioFetchFail:Failed to fetch audio. Please try again.`);
        this.cdr.markForCheck();
      }
    });
  }

  togglePlay(): void {
    if (this.wavesurfer) {
      this.wavesurfer.playPause();
    }
  }

  changePlaybackRate(): void {
    const rates = [1.0, 1.5, 2.0, 0.5];
    const currentIndex = rates.indexOf(this.playbackRate);
    this.playbackRate = rates[(currentIndex + 1) % rates.length];

    if (this.wavesurfer) {
      this.wavesurfer.setPlaybackRate(this.playbackRate);
    }
  }

  skipBackward(): void {
    if (this.wavesurfer) {
      const newTime = Math.max(0, this.wavesurfer.getCurrentTime() - 5);
      this.wavesurfer.setTime(newTime);
    }
  }
 
  skipForward(): void {
    if (this.wavesurfer) {
      const newTime = Math.min(this.duration, this.wavesurfer.getCurrentTime() + 10);
      this.wavesurfer.setTime(newTime);
    }
  }

  private copyTranscript(): void {
    if (!this.session?.history) return;
    const transcript = this.session.history
      .filter(m => (m.kind ?? 'Message') === 'Message' && m.content)
      .map(m => `${m.role ?? ''}: ${m.content}`)
      .join('\n');
    copy(transcript).then(() => {
      this.notificationService.showSuccess($localize`:@@convDetail.notify.transcriptCopied:Transcript copied to clipboard`);
    }).catch((err) => {
      console.error('Failed to copy transcript:', err);
      this.notificationService.showError($localize`:@@convDetail.notify.transcriptCopyFail:Failed to copy transcript`);
    });
  }

  copySessionId(): void {
    if (this.sessionId) {
      navigator.clipboard.writeText(this.sessionId);
      this.notificationService.showSuccess($localize`:@@convDetail.notify.sessionIdCopied:Conversation ID copied to clipboard`);
    }
  }

  /** Публичный share-URL сессии: абсолютная ссылка на наш appUrl с
   *  session_token в query. Работает как в admin, так и в widget/public
   *  режиме — токен из session DTO даёт read-only доступ к этой сессии
   *  без user-JWT. Формат ровно как /published/agents/:id/conversations/:sid
   *  из agent-test'а (showSessionEndedBanner). */
  copySessionUrl(): void {
    const token = this.session?.session_token;
    if (!this.agentId || !this.sessionId || !token) return;
    const url = `${environment.appUrl}/published/agents/${this.agentId}/conversations/${this.sessionId}?sessionToken=${encodeURIComponent(token)}`;
    copy(url).then(() => {
      this.notificationService.showSuccess($localize`:@@convDetail.notify.sessionUrlCopied:Session URL copied to clipboard`);
    }).catch((err) => {
      console.error('Failed to copy session URL:', err);
      this.notificationService.showError($localize`:@@convDetail.notify.sessionUrlCopyFail:Failed to copy session URL`);
    });
  }

  /** Create-test-from-message: обрезаем историю до кликнутого сообщения
   *  включительно и уводим на /tests/new с этим срезом в router state.
   *  test-edit прочитает history.state.seedMessages в create-режиме и
   *  предзаполнит seed_messages. Бэк тут не трогаем — юзер сам сохранит
   *  тест из test-edit'а если захочет. */
  createTestFromMessage(msg: ChatMessageDto): void {
    const history = this.session?.history ?? [];
    // Сравниваем по id если есть, иначе — по ссылке (id может быть
    // пустым у старых записей). Инклюзивный срез — «начиная с этого
    // сообщения» юзер воспринимает как «включая его».
    const idx = msg.id
      ? history.findIndex(m => m.id === msg.id)
      : history.indexOf(msg);
    if (idx < 0) return;
    const seedMessages = history.slice(0, idx + 1);
    this.router.navigate(['/tests/new'], { state: { seedMessages } });
  }

  seekToMessage(msg: ChatMessageDto): void {
    // Prefer real speech start time when present, otherwise fall back to message created_at.
    const tsCandidate = msg.metrics?.started_speaking_at ?? msg.created_at;
    if (!tsCandidate || !this.wavesurfer || !this.session) return;

    try {
      const turnTime = new Date(tsCandidate).getTime();

      let audioStartTime: number;
      if (this.session.started_at) {
        audioStartTime = new Date(this.session.started_at).getTime();
      } else if (this.session.history?.length && this.session.history[0].created_at) {
        audioStartTime = new Date(this.session.history[0].created_at).getTime();
      } else {
        return;
      }

      let offsetSeconds = (turnTime - audioStartTime) / 1000;

      // For user turns, rewind 4 seconds to hear the user's speech onset.
      if (msg.role === 'User') {
        offsetSeconds = Math.max(0, offsetSeconds - 4);
      }

      const seekPosition = Math.max(0, Math.min(offsetSeconds, this.duration));
      this.wavesurfer.setTime(seekPosition);

      if (!this.isPlaying) {
        this.wavesurfer.play();
      }
    } catch (error) {
      console.error('Failed to seek to message:', error);
    }
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  isTestSession(): boolean {
    return !!this.session?.test_id;
  }

  /**
   * Есть ли что проигрывать.
   *
   * Раньше плеер просто прятали у всех тест-сессий, и это было верно,
   * пока тесты были text-only. E2E — настоящая сессия с комнатой и
   * записью, прятать её нечего: слушать прогон нужно ровно так же, как
   * обычный разговор. Симуляция записи не имеет, и плеер там по-прежнему
   * не появится — но теперь по факту отсутствия аудио, а не по типу
   * сессии.
   */
  hasAudio(): boolean {
    return !!this.session?.audio_url || this.session?.has_audio === true;
  }

  testStatusLabel(status?: TestStatus | null): string {
    switch (status) {
      case 'Success': return $localize`:@@tests.runs.status.success:Success`;
      case 'Failed': return $localize`:@@tests.runs.status.failed:Failed`;
      case 'Processing': return $localize`:@@tests.runs.status.processing:Processing`;
      case 'Initial': return $localize`:@@tests.runs.status.initial:Initial`;
      default: return '-';
    }
  }

  testStatusSeverity(status?: TestStatus | null): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case 'Success': return 'success';
      case 'Failed': return 'danger';
      case 'Processing': return 'warn';
      default: return 'secondary';
    }
  }

  sessionStatusLabel(status?: AgentSessionStatus | null): string {
    switch (status) {
      case 'Pending': return $localize`:@@conversations.status.pending:Pending`;
      case 'Dispatching': return $localize`:@@conversations.status.dispatching:Dispatching`;
      case 'Active': return $localize`:@@conversations.status.active:Active`;
      case 'Completed': return $localize`:@@conversations.status.completed:Completed`;
      case 'Failed': return $localize`:@@conversations.status.failed:Failed`;
      case 'Cancelled': return $localize`:@@conversations.status.cancelled:Cancelled`;
      case 'Timeout': return $localize`:@@conversations.status.timeout:Timeout`;
      default: return $localize`:@@conversations.status.unknown:Unknown`;
    }
  }

  sessionStatusSeverity(
    status?: AgentSessionStatus | null,
  ): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch (status) {
      case 'Completed': return 'success';
      case 'Active':
      case 'Dispatching': return 'info';
      case 'Failed': return 'danger';
      case 'Timeout': return 'warn';
      case 'Cancelled':
      case 'Pending':
      default: return 'secondary';
    }
  }

  evalCompletionReasonLabel(reason?: EvalCompletionReason | null): string {
    switch (reason) {
      case 'StepsExhausted': return $localize`:@@eval.reason.stepsExhausted:Steps exhausted`;
      case 'JudgeDecided': return $localize`:@@eval.reason.judgeDecided:Judge decided`;
      case 'EndCallInvoked': return $localize`:@@eval.reason.endCallInvoked:End call invoked`;
      // Unknown/future enum values — show the raw value rather than nothing.
      default: return reason ?? '-';
    }
  }
}
